// `GET /v1/agents/activity` (#5206) — the one read the Agents page polls. The entity
// and the fold that builds it are `value-objects/agent-activity.ts`; this is its
// published projection, shared by the route and the dashboard client so neither
// holds a mirror.
//
// STRICT, both levels. The mode flag is the one field of an agent mode this read
// exposes (epic #5200 D11): a `code_tools`, `ceiling` or `revision` reaching this
// body would publish, under `read:tasks`, what `/v1/agent-modes` keeps behind
// `write:prompts`. A strict schema makes that a parse failure at the seam — in the
// resource's golden test and in the client that parses every body — rather than a
// field nobody noticed.

import { z } from 'zod';
import type { AgentActivity } from '../value-objects/agent-activity';
import { AGENT_IDS, type AgentId } from '../value-objects/agent-roster';
import { defineResource } from './resource';

const CountSchema = z.number().int().nonnegative();

export const AgentActivityEntryWireSchema = z
  .object({
    id: z
      .enum(AGENT_IDS as [AgentId, ...AgentId[]])
      .describe('The roster agent (`AGENT_IDS`). Every agent appears exactly once.'),
    running: CountSchema.nullable().describe(
      'Tasks of this agent in a running status (assigned, in_progress) now; for triage, asks under a live claim. Null when the agent has no per-run record (gardener, escalation, default), never zero.',
    ),
    last_run_iso: z
      .string()
      .nullable()
      .describe(
        'When the agent last ran (ISO 8601): the newest task update in a running, worked or failed status; for the gardener its newest consolidation over enabled repos; for escalation its newest escalated routing decision; for triage its newest decided ask. Null when there is none.',
      ),
    window_7d: z
      .object({
        worked: CountSchema.describe(
          'Tasks updated in the last 7 days in a worked status (completed, awaiting_ci, awaiting_review, mergeable, merged); for escalation, escalated routing decisions; for triage, asks decided. Cancelled counts neither way.',
        ),
        failed: CountSchema.nullable().describe(
          'Tasks updated in the last 7 days in the failed status. Null for escalation and triage, which decide rather than run.',
        ),
      })
      .strict()
      .nullable()
      .describe('The last 7 days, bucketed. Null when the agent has no per-run record to count.'),
    held: CountSchema.optional().describe(
      "Asks held now on this agent's model selection (a key or model its Agents-page card picks). Present on triage only.",
    ),
    mode_enabled: z
      .boolean()
      .optional()
      .describe(
        "Whether the agent's mode is switched on at org scope. Present only on the agent that owns a mode and the agents that mode also governs; the mode's tool lists are never exposed here.",
      ),
    machines: z
      .object({
        on: CountSchema.describe(
          "The account's own org machines that are online, not drained, and able to run this agent's role now: its machine switch on, its org mode on, and a box role that can run it. Only for a caller holding `write:prompts` does it also leave out a machine whose reported build cannot apply the org's tool narrowing.",
        ),
        total: CountSchema.describe(
          "The account's own org machines on the Fleet page (not revoked; a pending one counts), the same rows that carry `roles` on `GET /v1/fleet/utilization`.",
        ),
      })
      .strict()
      .nullable()
      .describe(
        "On how many of the account's own org machines this agent's role runs, of how many. Null for an agent whose role no machine switch can set yet (and Default), and on every agent when machine presence could not be read — never `on: 0` in that case.",
      ),
  })
  .strict();

export const AgentActivityWireSchema = z
  .object({
    agents: z
      .array(AgentActivityEntryWireSchema)
      .describe('One entry per roster agent, in roster order.'),
  })
  .strict();

export type AgentActivityWire = z.infer<typeof AgentActivityWireSchema>;
export type AgentActivityEntryWire = z.infer<typeof AgentActivityEntryWireSchema>;

export const AgentActivityResource = defineResource<AgentActivity, typeof AgentActivityWireSchema>({
  schema: AgentActivityWireSchema,
  serialize: (a) => ({
    agents: a.agents.map((e) => ({
      id: e.id,
      running: e.running,
      last_run_iso: e.lastRunAt === null ? null : e.lastRunAt.toISOString(),
      window_7d:
        e.window7d === null ? null : { worked: e.window7d.worked, failed: e.window7d.failed },
      ...(e.held === undefined ? {} : { held: e.held }),
      ...(e.modeEnabled === undefined ? {} : { mode_enabled: e.modeEnabled }),
      machines: e.machines === null ? null : { on: e.machines.on, total: e.machines.total },
    })),
  }),
});
